/**
 * @file object-form.component.tsx
 * @description Компонент формы объекта для Task Manager приложения, который обеспечивает полнофункциональный интерфейс создания и редактирования объектов различных типов. Включает поддержку простых и табовых форм через SimpleBody и TabsBody, интеграцию с react-hook-form для управления состоянием формы, горячие клавиши для сохранения (Ctrl+S) и закрытия (Escape), блокировку навигации при несохраненных изменениях, диалоги подтверждения, систему управления модальными окнами и автоматическую синхронизацию с сервером через ObjectFormService.
 * @author AI-Generated | Human-Reviewed
 * @created 2025-01-15
 * @updated 2025-01-15
 * @dependencies ['react', './components', '@components/ui/layout/page-modal', '@consta/uikit/useGlobalKeys', '@api/actions/object-form', 'react-hook-form', '@forms/default-object', '@components/ui/layout/dialog-modal', '@components/ui/layout/modal', 'react-router-dom', 'react-redux', '@store/slices/modals-queue-slice', '@src/hooks', '@components/ui/base/text', '@src/types']
 * @related-files ['src/features/object-form/components/simple-body.component.tsx', 'src/features/object-form/components/tabs-body.component.tsx', 'src/api/actions/object-form.ts', 'src/forms/default-object']
 */

import { useEffect, useState, SyntheticEvent, useId, useRef, useCallback } from 'react';
import { Header, SimpleBody, TabsBody } from './components';
import { PageModal } from '@components/ui/layout/page-modal';
import { useGlobalKeys } from '@consta/uikit/useGlobalKeys';
import { ObjectFormService } from '@api/actions/object-form';
import { useForm } from 'react-hook-form';
import { Form } from '@forms/default-object';
import { DialogModal } from '@components/ui/layout/dialog-modal';
import { Modal } from '@components/ui/layout/modal';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { addItem, removeItem, setItems, getItems } from '@store/slices/modals-queue-slice';
import { useNavBlocker } from '@src/hooks';
import { Text } from '@components/ui/base/text';
import { tabObject, TBaseObjectDto } from '@src/types';
import { TextField } from '@components/form-controls/text-field';
import { IconForward } from '@consta/icons/IconForward';
import { StateBadge } from '@components/ui/indicators/state-badge';
import { ErrorList, ErrorListHandle } from '@components/ui/indicators/error-list';
import { Loader } from '@components/ui';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { setErrors } from '@utils/error-helper';

interface IObjectForm {
    container?: HTMLDivElement;
    initialItem?: TBaseObjectDto;
    type: string;
    form?: any;
    tabs?: tabObject[] | null;
    onClose?: () => void;
    onChange?: (result: any) => void;
    afterSave?: (result: any) => void;
}

//TODO: переделать получение данных об entity через type и useMemo
export const ObjectForm = ({
    container,
    initialItem = {},
    type,
    form = Form,
    tabs = null,
    onClose,
    onChange,
    afterSave
}: IObjectForm) => {
    const [isShow, setIsShow] = useState<boolean>(true);
    const [isShowDialogModal, setIsShowDialogModal] = useState<boolean>(false);
    const [isShowRefreshModal, setIsShowRefreshModal] = useState<boolean>(false);
    const [isShowHistoryCommentModal, setIsShowHistoryCommentModal] = useState<boolean>(false);
    const [item, setItem] = useState<any>(null);
    const errorListRef = useRef<ErrorListHandle | null>(null);

    const {
        handleSubmit,
        control,
        reset,
        setValue,
        getValues,
        watch,
        setError,
        clearErrors,
        formState: { isDirty, isSubmitting, errors }
    } = useForm();

    useNavBlocker({ onBlock: () => console.log('block tab'), enabled: isDirty });

    const dispatchRedux = useDispatch();
    const modalId: string = useId();
    const modalItems: string[] = useSelector(getItems);
    const navigate = useNavigate();
    const modalItemsRef = useRef(modalItems);
    const isCloseAfterRef = useRef(false);
    const itemRef = useRef(item);

    useGlobalKeys({
        Escape: () => tryClosePage()
    });

    const tryClosePage = useCallback(
        (isForce: boolean = false) => {
            if (modalItemsRef.current.at(-1) !== modalId) {
                return;
            }

            if (!isForce && isDirty) {
                isCloseAfterRef.current = true;
                setIsShowDialogModal(true);
                return;
            }

            closePageHandler();
        },
        [isDirty]
    );

    useEffect(() => {
        fetchData(type);
        dispatchRedux(addItem(modalId));

        return () => {
            closePageHandler();
        };
    }, []);

    async function fetchData(name: string) {
        const response: any = await ObjectFormService.get(name, initialItem.id);

        if (axios.isAxiosError(response)) {
            setErrors(response, errorListRef.current);
            return;
        }

        const combinedItem = { ...initialItem, ...response.data };
        setItem(combinedItem);
        reset(combinedItem);
    }

    useEffect(() => {
        modalItemsRef.current = modalItems;
    }, [modalItems]);

    useEffect(() => {
        itemRef.current = item;
    }, [item]);

    const closePageHandler = useCallback(() => {
        if (modalItemsRef.current.at(-1) !== modalId) {
            return;
        }

        dispatchRedux(removeItem(modalId));
        setIsShow(false);
        onClose && onClose();

        if (modalItemsRef.current.filter((modal: string) => modal !== modalId).length < 1) {
            dispatchRedux(setItems([]));
            navigate('/');
        }
    }, [modalId, dispatchRedux, onClose, navigate]);

    const onSubmit = async (data: any, isCloseAfter: boolean = false, isForce: boolean = false) => {
        isCloseAfterRef.current = isCloseAfter;

        if (
            !isForce &&
            tabs?.find((tabItem: tabObject) => tabItem.tab === 'histories' && data.state.id !== item.state.id)
        ) {
            setIsShowHistoryCommentModal(true);
            return;
        }

        const response: AxiosResponse | AxiosError =
            !data?.id || data.id === import.meta.env.VITE_EMPTY_GUID
                ? await ObjectFormService.create(type, data)
                : await ObjectFormService.save(type, data);

        if (axios.isAxiosError(response)) {
            setErrors(response, errorListRef.current);
            return;
        }

        setItem(response.data);
        onChange && onChange(response.data);
        afterSave && afterSave(response.data);
        reset({ ...data, ...response.data });
        isCloseAfter && tryClosePage(true);
    };

    const onPageRefresh = useCallback(() => {
        isDirty ? setIsShowRefreshModal(true) : fetchData(type);
    }, [isDirty, type]);

    const renderModals = () => (
        <>
            {isShowRefreshModal && (
                <DialogModal
                    container={container}
                    isShow={isShowRefreshModal}
                    setIsShow={setIsShowRefreshModal}
                    title="Обновление приведет к потере несохраненных данных, продолжить?"
                    onConfirm={() => fetchData(type)}
                />
            )}

            {isShowDialogModal && (
                <Modal
                    title="Сохранить внесенные изменения?"
                    container={container}
                    isShow={isShowDialogModal}
                    setIsShow={setIsShowDialogModal}
                    onConfirm={() =>
                        handleSubmit(async (data) => await onSubmit(data, isCloseAfterRef.current, true))()
                    }
                    onReject={() => {
                        dispatchRedux(removeItem(modalId));
                        setIsShow(false);
                        onClose && onClose();
                    }}
                    isWithCancel={true}
                    className="min-h-[17.9952vmin]"
                >
                    {getValues('state') && item.state?.id !== getValues('state').id && (
                        <div className="flex flex-col gap-[1.2703vmin]">
                            <Text className="text-[#002033]" size="s">
                                Изменён статус:
                            </Text>

                            <div className="flex items-center gap-[0.6961vmin]">
                                <StateBadge state={item.state} className="h-[1.9054vmin]" />

                                <IconForward size="xs" />

                                <StateBadge state={getValues('state')} className="h-[1.9054vmin]" />
                            </div>

                            <TextField
                                control={control}
                                required={true}
                                name="commentHistory"
                                label="Комментарий"
                                type="textarea"
                                minRows={14}
                                className="mb-[2.0112vmin]"
                            />
                        </div>
                    )}
                </Modal>
            )}

            {isShowHistoryCommentModal && (
                <Modal
                    title="Изменён статус"
                    container={container}
                    isShow={isShowHistoryCommentModal}
                    setIsShow={setIsShowHistoryCommentModal}
                    onConfirm={() =>
                        handleSubmit(async (data) => await onSubmit(data, isCloseAfterRef.current, true))()
                    }
                    isWithCancel={true}
                    confirmLabel="OK"
                    rejectLabel={null}
                    className="min-h-[17.9952vmin]"
                >
                    {getValues('state') && item.state?.id !== getValues('state').id && (
                        <div className="flex flex-col gap-[1.2703vmin]">
                            <div className="flex items-center gap-[0.6961vmin]">
                                <StateBadge state={item.state} className="h-[1.9054vmin]" />

                                <IconForward size="xs" />

                                <StateBadge state={getValues('state')} className="h-[1.9054vmin]" />
                            </div>

                            <TextField
                                control={control}
                                required={true}
                                name="commentHistory"
                                label="Комментарий"
                                type="textarea"
                                minRows={14}
                                className="mb-[2.0112vmin]"
                            />
                        </div>
                    )}
                </Modal>
            )}
        </>
    );

    return (
        <PageModal container={container} closePageHandler={tryClosePage} isOpen={isShow}>
            <div className="objectForm gap-default h-full w-full relative">
                <div className="objectForm__wrapper absolute w-full h-full flex flex-col">
                    <form onSubmit={handleSubmit((data: any) => onSubmit(data))} className="h-full flex flex-col">
                        <Header
                            isChanged={isDirty}
                            isLoading={isSubmitting}
                            control={control}
                            number={item?.number || item?.code}
                            closePageHandler={tryClosePage}
                            item={item}
                            container={container}
                            setValue={setValue}
                            saveObjectHandler={(_e: SyntheticEvent, isCloseAfter: boolean = false) =>
                                handleSubmit(async (data) => await onSubmit(data, isCloseAfter))()
                            }
                            onPageRefresh={onPageRefresh}
                            isSimple={!Boolean(tabs)}
                        />

                        {item ? (
                            tabs ? (
                                <TabsBody
                                    tabs={tabs}
                                    item={item}
                                    watch={watch}
                                    control={control}
                                    setValue={setValue}
                                    setError={setError}
                                    formErrors={errors}
                                    getValues={getValues}
                                    clearErrors={clearErrors}
                                    form={form}
                                    errorListRef={errorListRef}
                                />
                            ) : (
                                <SimpleBody
                                    item={item}
                                    watch={watch}
                                    control={control}
                                    setValue={setValue}
                                    setError={setError}
                                    formErrors={errors}
                                    getValues={getValues}
                                    clearErrors={clearErrors}
                                    form={form}
                                    errorListRef={errorListRef}
                                />
                            )
                        ) : (
                            <Loader />
                        )}
                    </form>
                </div>

                {renderModals()}

                <ErrorList ref={errorListRef} />
            </div>
        </PageModal>
    );
};
